/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import { Redirect, Switch, Route, useLocation } from 'react-router-dom';
import { shallowEqual, useSelector } from 'react-redux';
import { SnackbarProvider } from 'notistack';
import ReactGA from 'react-ga4';
import { Layout } from '../_metronic/layout';
import BasePage from './BasePage';
import { Logout, AuthPage } from './modules/Auth';
import ErrorsPage from './modules/ErrorsExamples/ErrorsPage';
import { PermissionProvider } from './contexts/PermissionsContext';
import { DashboardProvider } from './contexts/DashboardContext';
import { WebsocketProvider } from './contexts/WebsocketContext';
import { BreadcrumbProvider } from './contexts/BreadcrumbContext';
import NotFound from './pages/SpecialPages/NotFound';
import MaintenancePage from './pages/SpecialPages/Maintenance';
import { LocaleProvider } from './contexts/LocaleContext';

export function Routes() {
  const user = useSelector(({ auth }) => auth.user, shallowEqual);
  const isAuthorized = user != null;
  const location = useLocation();

  useEffect(() => {
    if (isAuthorized) {
      ReactGA.initialize('G-B98BPRN8R3', {});
      ReactGA.set({
        user_id: user.userId,
        user_properties: {
          username: user.fullname,
          email: user.email,
          account_id: user.entityId,
          account_name: user.entityName,
        },
      });
    }
  }, [isAuthorized]);

  useEffect(() => {
    if (isAuthorized) {
      ReactGA.send({
        hitType: 'pageview',
        page: location.pathname + location.search,
      });
    }
  }, [isAuthorized, location]);

  return (
    <DashboardProvider>
      <Switch>
        {!isAuthorized ? (
          /*Render auth page when user at `/auth` and not authorized.*/
          <Route>
            <AuthPage />
          </Route>
        ) : (
          /*Otherwise redirect to root page (`/`)*/
          <Redirect from="/auth" to="/" />
        )}

        <Route path="/error" component={ErrorsPage} />
        <Route path="/404" component={NotFound} />
        <Route path="/logout" component={Logout} />
        <Route path="/maintenance" component={MaintenancePage} />

        {!isAuthorized ? (
          /*Redirect to `/auth` when user is not authorized*/
          <Redirect to="/auth/login" />
        ) : (
          <PermissionProvider>
            <WebsocketProvider>
              <LocaleProvider>
                <BreadcrumbProvider>
                  <SnackbarProvider
                    anchorOrigin={{
                      horizontal: 'right',
                      vertical: 'top',
                    }}
                    hideIconVariant
                  >
                    <Layout>
                      <BasePage />
                    </Layout>
                  </SnackbarProvider>
                </BreadcrumbProvider>
              </LocaleProvider>
            </WebsocketProvider>
          </PermissionProvider>
        )}
      </Switch>
    </DashboardProvider>
  );
}
